import { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AdminOnly } from './components/AdminOnly';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Play } from './pages/Play';
import { LocalGamePage } from './pages/LocalGame';
import { OnlineGame } from './pages/OnlineGame';
import { FourGamePage } from './pages/FourGame';
import { Puzzles } from './pages/Puzzles';
import { Learn } from './pages/Learn';
import { RuleEditor } from './pages/RuleEditor';
import { Profile } from './pages/Profile';
import { ForgotPassword, Login } from './pages/Login';
import { SettingsPage } from './pages/Settings';
import { applyTheme, settings } from './lib/store';
import { startAutoSync } from './lib/sync';
import { refreshMe } from './lib/auth';
import { refreshModes } from './lib/modes';
import './styles.css';
import './controls.css';
import './game.css';
import './layout.css';
import './motion.css';
import './anim.css';
import './feedback.css';

applyTheme(settings.get().theme);
settings.subscribe(() => applyTheme(settings.get().theme));

const HoodApp = lazy(() => import('./hood/HoodApp').then((m) => ({ default: m.HoodApp })));
const AdminApp = lazy(() => import('./admin/AdminApp').then((m) => ({ default: m.AdminApp })));
const WarApp = lazy(() => import('./war/WarApp').then((m) => ({ default: m.WarApp })));

const router = createBrowserRouter([
  // War Royale is a game of its own: full screen, outside the IllegalChess layout.
  { path: '/hood', element: <Suspense fallback={<div className="page page-loading" />}><HoodApp /></Suspense> },
  { path: '/admin', element: <Suspense fallback={<div className="page page-loading" />}><AdminApp /></Suspense> },
  { path: '/war', element: <Suspense fallback={<div className="page page-loading" />}><WarApp /></Suspense> },
  {
    element: <Layout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/play', element: <Home /> },
      { path: '/play/setup', element: <Play /> },
      { path: '/play/:mode', element: <LocalGamePage /> },
      { path: '/sandbox', element: <LocalGamePage sandbox /> },
      { path: '/game/:id', element: <OnlineGame /> },
      { path: '/four/:id', element: <FourGamePage /> },
      { path: '/puzzles', element: <Puzzles /> },
      { path: '/learn', element: <Learn /> },
      { path: '/editor', element: <AdminOnly><RuleEditor /></AdminOnly> },
      { path: '/profile', element: <Profile /> },
      { path: '/profile/:name', element: <Profile /> },
      { path: '/login', element: <Login /> },
      { path: '/forgot', element: <ForgotPassword /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '*', element: <Home /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(<RouterProvider router={router} />);

startAutoSync();
void refreshMe();
void refreshModes();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => {}); });
}
